import { useMemo } from "react";

import { resolveNewThreadProviderInventoryView } from "./NewThreadLanding.logic";
import {
  selectLastKnownInventoryPublishedAt,
  selectLastKnownProvidersForEnvironment,
} from "./lastKnownProviderCatalog";

type InventoryViewInput = Parameters<typeof resolveNewThreadProviderInventoryView>[0];

/**
 * What the chosen environment has published, as published.
 *
 * This used to return an eligible subset alongside the inventory, and the
 * picker read the subset. Since FOR-36365 the picker is handed the inventory
 * and a reason per provider, so filtering here would only hide the rows the
 * reasons exist to explain. Submission still fails closed through the launch
 * gate, one layer down.
 */
export function useDraftProviderInventory(input: {
  readonly targetEnvironmentId: InventoryViewInput["targetEnvironmentId"];
  readonly primaryEnvironmentId: InventoryViewInput["primaryEnvironmentId"];
  readonly primaryServerConfig: InventoryViewInput["primaryServerConfig"];
  readonly targetEnvironmentServerConfig: InventoryViewInput["targetEnvironmentServerConfig"];
  readonly runtimes: Parameters<typeof selectLastKnownProvidersForEnvironment>[0]["runtimes"];
  /** This install's CMMD device runtime id, the other name for the primary server. */
  readonly localDeviceRuntimeId: string | null;
  readonly runtimeId?: string | null;
}) {
  const {
    targetEnvironmentId,
    primaryEnvironmentId,
    primaryServerConfig,
    targetEnvironmentServerConfig,
    runtimes,
    localDeviceRuntimeId,
    runtimeId = null,
  } = input;

  const lastKnownProviders = useMemo(
    () => selectLastKnownProvidersForEnvironment({ targetEnvironmentId, runtimes, runtimeId }),
    [targetEnvironmentId, runtimes, runtimeId],
  );

  const lastKnownInventoryPublishedAt = useMemo(
    () => selectLastKnownInventoryPublishedAt({ targetEnvironmentId, runtimes, runtimeId }),
    [targetEnvironmentId, runtimes, runtimeId],
  );

  const providerInventory = useMemo(
    () =>
      resolveNewThreadProviderInventoryView({
        targetEnvironmentId,
        primaryEnvironmentId,
        primaryServerConfig,
        targetEnvironmentServerConfig,
        lastKnownProviders,
        lastKnownInventoryPublishedAt,
        localDeviceRuntimeId,
      }),
    [
      lastKnownInventoryPublishedAt,
      lastKnownProviders,
      localDeviceRuntimeId,
      primaryEnvironmentId,
      primaryServerConfig,
      targetEnvironmentId,
      targetEnvironmentServerConfig,
    ],
  );

  const providerStatuses = providerInventory.providerStatuses;

  return { providerInventory, providerStatuses };
}
